import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Label,
  ListBox,
  Modal,
  NumberField,
  Select,
  Switch,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const GRANT_MODES = ['PER_PERIOD', 'CONTINUOUS'] as const;

export const GRANT_MODE_LABELS: Record<(typeof GRANT_MODES)[number], string> = {
  PER_PERIOD: '每周期',
  CONTINUOUS: '持续',
};

const entitlementSchema = z.object({
  resourceDefinitionId: z.string().min(1, '请选择资源'),
  grantMode: z.enum(GRANT_MODES),
  baseQuantity: z.coerce.number().int().min(0, '单位发放量不能为负'),
  isMultiplyBySeat: z.boolean(),
});

type EntitlementFormValues = z.infer<typeof entitlementSchema>;

export type SkuEntitlementTemplateFormItem = {
  id?: string;
  skuId?: string | null;
  resourceDefinitionId?: string | null;
  grantMode?: string | null;
  baseQuantity?: number | string | null;
  isMultiplyBySeat?: boolean | number | null;
  resourceDefinition?: {
    id?: string;
    code?: string | null;
    name?: string | null;
  } | null;
};

interface SkuEntitlementTemplateEditorModalProps {
  open: boolean;
  skuId: string;
  skuName?: string;
  item?: SkuEntitlementTemplateFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

function toNumber(value: number | string | null | undefined, fallback = 0): number {
  if (value == null || value === '') return fallback;
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(n) ? n : fallback;
}

function buildDefaultValues(
  item?: SkuEntitlementTemplateFormItem | null
): EntitlementFormValues {
  if (item) {
    return {
      resourceDefinitionId: String(item.resourceDefinitionId ?? item.resourceDefinition?.id ?? ''),
      grantMode: (GRANT_MODES as readonly string[]).includes(item.grantMode ?? '')
        ? (item.grantMode as (typeof GRANT_MODES)[number])
        : 'CONTINUOUS',
      baseQuantity: toNumber(item.baseQuantity, 0),
      isMultiplyBySeat: Boolean(item.isMultiplyBySeat),
    };
  }
  return {
    resourceDefinitionId: '',
    grantMode: 'CONTINUOUS',
    baseQuantity: 0,
    isMultiplyBySeat: false,
  };
}

export function SkuEntitlementTemplateEditorModal({
  open,
  skuId,
  skuName,
  item,
  onCancel,
  onSuccess,
}: SkuEntitlementTemplateEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = Boolean(item?.id);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const { data: resources = [] } = trpc.bizResource.listAll.useQuery({}, { enabled: open });

  const form = useForm<EntitlementFormValues>({
    resolver: zodResolver(entitlementSchema) as any,
    defaultValues: buildDefaultValues(item),
    mode: 'onChange',
  });
  const { reset, control } = form;
  const errors = form.formState.errors;

  useEffect(() => {
    if (!open) {
      reset(buildDefaultValues());
      return;
    }
    reset(buildDefaultValues(item));
  }, [open, item, reset]);

  const createMutation = trpc.bizProduct.skuEntitlementTemplate.create.useMutation({
    onSuccess: () => {
      toast.success('权益已创建');
      form.reset();
      onSuccess();
      void utils.bizProduct.skuEntitlementTemplate.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('创建失败', { description: error.message });
    },
  });

  const updateMutation = trpc.bizProduct.skuEntitlementTemplate.update.useMutation({
    onSuccess: () => {
      toast.success('权益已更新');
      form.reset();
      onSuccess();
      void utils.bizProduct.skuEntitlementTemplate.listAll.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', { description: error.message });
    },
  });

  const onSubmit = async (values: EntitlementFormValues) => {
    const payload = {
      resourceDefinitionId: values.resourceDefinitionId,
      grantMode: values.grantMode,
      baseQuantity: values.baseQuantity,
      isMultiplyBySeat: values.isMultiplyBySeat,
    };

    if (isEdit && item?.id) {
      await updateMutation.mutateAsync({ id: item.id, data: payload });
    } else {
      await createMutation.mutateAsync({ skuId, ...payload });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg" className="max-w-2xl">
          <Modal.Dialog
            key={open ? (isEdit && item?.id ? `edit-${item.id}` : 'create') : 'closed'}
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑权益' : '添加权益'}
              </Modal.Heading>
              <Description className="text-sm">
                {skuName ? `SKU：${skuName}` : '配置 SKU 权益模板'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-sku-entitlement-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={control}
                  name="resourceDefinitionId"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.resourceDefinitionId}
                      variant="secondary"
                      value={field.value || undefined}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        资源 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {resources.map((r) => (
                            <ListBox.Item
                              key={String(r.id)}
                              id={String(r.id)}
                              textValue={`${r.name ?? ''} ${r.code ?? ''}`}
                            >
                              {r.name}
                              {r.code ? `（${r.code}）` : ''}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.resourceDefinitionId ? (
                        <FieldError>{errors.resourceDefinitionId.message}</FieldError>
                      ) : null}
                    </Select>
                  )}
                />

                <div className="grid gap-4 sm:grid-cols-2">
                  <Controller
                    control={control}
                    name="grantMode"
                    render={({ field }) => (
                      <Select
                        className="w-full"
                        isInvalid={!!errors.grantMode}
                        variant="secondary"
                        value={field.value}
                        onChange={(key) => {
                          if (key != null) field.onChange(String(key));
                        }}
                      >
                        <Label className="text-sm font-medium">
                          发放方式 <span className="text-danger">*</span>
                        </Label>
                        <Select.Trigger>
                          <Select.Value />
                          <Select.Indicator />
                        </Select.Trigger>
                        <Select.Popover>
                          <ListBox>
                            {GRANT_MODES.map((m) => (
                              <ListBox.Item key={m} id={m} textValue={GRANT_MODE_LABELS[m]}>
                                {GRANT_MODE_LABELS[m]}
                              </ListBox.Item>
                            ))}
                          </ListBox>
                        </Select.Popover>
                        {errors.grantMode ? (
                          <FieldError>{errors.grantMode.message}</FieldError>
                        ) : null}
                      </Select>
                    )}
                  />

                  <Controller
                    control={control}
                    name="isMultiplyBySeat"
                    render={({ field }) => (
                      <div className="flex flex-col gap-2">
                        <Label className="text-sm font-medium">是否乘以席位</Label>
                        <Switch
                          isSelected={field.value}
                          onChange={(selected) => field.onChange(selected)}
                        >
                          <Switch.Content>
                            <Switch.Control>
                              <Switch.Thumb />
                            </Switch.Control>
                            <span className="text-sm">{field.value ? '是' : '否'}</span>
                          </Switch.Content>
                        </Switch>
                        <Description className="text-xs">
                          是：单位发放量 × 份数 × 席位；否：单位发放量 × 份数。订单份数始终相乘。
                        </Description>
                      </div>
                    )}
                  />
                </div>

                <Controller
                  control={control}
                  name="baseQuantity"
                  render={({ field }) => (
                    <NumberField
                      className="w-full"
                      variant="secondary"
                      isInvalid={!!errors.baseQuantity}
                      minValue={0}
                      value={field.value}
                      onChange={(v) => field.onChange(v ?? 0)}
                    >
                      <Label className="text-sm font-medium">
                        单位发放量 <span className="text-danger">*</span>
                      </Label>
                      <NumberField.Group>
                        <NumberField.DecrementButton />
                        <NumberField.Input className="w-full" />
                        <NumberField.IncrementButton />
                      </NumberField.Group>
                      <Description className="text-xs">单位由资源定义决定；存储用字节</Description>
                      {errors.baseQuantity ? (
                        <FieldError>{errors.baseQuantity.message}</FieldError>
                      ) : null}
                    </NumberField>
                  )}
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
